import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Minus, Plus, Trash2, ShoppingBag } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import { useTheme } from '../context/ThemeContext';
import { formatDualPrice } from '../utils/priceUtils';
import { formatMeasure } from '../utils/measureUtils';

const CartDrawer = () => {
  const { t, getName, language } = useLanguage();
  const { items, isOpen, closeCart, updateQuantity, removeItem, totalPrice, totalItems } = useCart();
  const { isDark } = useTheme();

  // Use image proxy for pizzadonvito.com images
  const getImageUrl = (url) => {
    if (url && url.includes('pizzadonvito.com')) {
      return `${process.env.REACT_APP_BACKEND_URL}/api/image-proxy?url=${encodeURIComponent(url)}`;
    }
    return url;
  };

  const sizeLabels = {
    '24cm': { bg: 'Малка', en: 'Small' },
    '30cm': { bg: 'Голяма', en: 'Large' },
    '60cm': { bg: 'Семейна', en: 'Family' },
    'default': { bg: '', en: '' },
    'chocolate': { bg: 'Шоколад', en: 'Chocolate' },
    'jam': { bg: 'Сладко', en: 'Jam' },
    'honey': { bg: 'Мед', en: 'Honey' },
    'cheese': { bg: 'Сирене', en: 'Cheese' }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            data-testid="cart-backdrop"
            className={`fixed inset-0 backdrop-blur-sm z-50 ${isDark ? 'bg-black/60' : 'bg-black/40'}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeCart}
          />

          {/* Drawer */}
          <motion.div
            data-testid="cart-drawer"
            className={`fixed top-0 right-0 h-full w-full max-w-md border-l z-50 flex flex-col shadow-2xl ${
              isDark 
                ? 'bg-[#0A0A0A] border-white/10' 
                : 'bg-white border-gray-200'
            }`}
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
          >
            {/* Header */}
            <div className={`flex items-center justify-between p-6 border-b ${
              isDark ? 'border-white/10' : 'border-gray-200'
            }`}>
              <div className="flex items-center gap-3">
                <ShoppingBag className="text-[#C8102E]" size={24} />
                <h2 className={`font-cormorant text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {t('your_order')}
                </h2>
                {totalItems > 0 && (
                  <span className="bg-[#C8102E] text-white text-sm font-bold px-2.5 py-0.5 rounded-full">
                    {totalItems}
                  </span>
                )}
              </div>
              <button
                data-testid="close-cart-btn"
                onClick={closeCart}
                className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                  isDark 
                    ? 'bg-white/10 text-white/80 hover:text-white hover:bg-white/20' 
                    : 'bg-gray-100 text-gray-600 hover:text-gray-900 hover:bg-gray-200'
                }`}
              >
                <X size={20} />
              </button>
            </div>

            {/* Cart Items */}
            <div className="flex-1 overflow-y-auto p-6">
              {items.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center">
                  <ShoppingBag className={`mb-4 ${isDark ? 'text-white/20' : 'text-gray-300'}`} size={64} />
                  <p className={`font-outfit ${isDark ? 'text-white/50' : 'text-gray-400'}`}>
                    {t('empty_cart')}
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <AnimatePresence mode="popLayout">
                    {items.map((item) => {
                      const sizeLabel = sizeLabels[item.selectedSize];
                      const displayedSize = sizeLabel
                        ? sizeLabel[language]
                        : formatMeasure(item.selectedSize, language);
                      
                      return (
                        <motion.div
                          key={`${item.id}-${item.selectedSize}`}
                          data-testid={`cart-item-${item.id}`}
                          className={`flex gap-4 rounded-xl p-4 border ${
                            isDark 
                              ? 'bg-white/5 border-white/10' 
                              : 'bg-gray-50 border-gray-200'
                          }`}
                          layout
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -20, height: 0, marginBottom: 0, padding: 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          {/* Image */}
                          <img
                            src={getImageUrl(item.image)}
                            alt={getName(item)}
                            className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
                            loading="lazy"
                            decoding="async"
                          />

                          {/* Details */}
                          <div className="flex-1 min-w-0">
                            <h4 className={`font-outfit font-semibold text-sm truncate ${
                              isDark ? 'text-white' : 'text-gray-900'
                            }`}>
                              {getName(item)}
                            </h4>
                            {displayedSize && item.selectedSize !== 'default' && (
                              <p className={`text-xs mt-0.5 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                                {displayedSize}
                              </p>
                            )}
                            <p className="text-[#C8102E] font-bold mt-2 text-sm">
                              {formatDualPrice(item.unitPrice * item.quantity).eur} € / {formatDualPrice(item.unitPrice * item.quantity).bgn} {t('currency')}
                            </p>

                            {/* Quantity Controls */}
                            <div className="flex items-center gap-3 mt-2">
                              <button
                                data-testid={`cart-decrease-${item.id}`}
                                onClick={() => updateQuantity(item.id, item.selectedSize, item.quantity - 1)}
                                className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ${
                                  isDark 
                                    ? 'bg-white/10 text-white hover:bg-white/20' 
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                              >
                                <Minus size={14} />
                              </button>
                              <span className={`font-medium w-6 text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                {item.quantity}
                              </span>
                              <button
                                data-testid={`cart-increase-${item.id}`}
                                onClick={() => updateQuantity(item.id, item.selectedSize, item.quantity + 1)}
                                className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ${
                                  isDark 
                                    ? 'bg-white/10 text-white hover:bg-white/20' 
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                              >
                                <Plus size={14} />
                              </button>
                            </div>
                          </div>

                          {/* Remove Button */}
                          <button
                            data-testid={`cart-remove-${item.id}`}
                            onClick={() => removeItem(item.id, item.selectedSize)}
                            className="flex-shrink-0 w-8 h-8 bg-red-500/20 rounded-full flex items-center justify-center text-red-400 hover:bg-red-500/30 transition-colors self-start"
                          >
                            <Trash2 size={14} />
                          </button>
                        </motion.div>
                      );
                    })}
                  </AnimatePresence>
                </div>
              )}
            </div>

            {/* Footer with Total */}
            {items.length > 0 && (
              <div className={`p-6 border-t ${
                isDark 
                  ? 'border-white/10 bg-[#121212]' 
                  : 'border-gray-200 bg-gray-50'
              }`}>
                <div className="flex items-center justify-between mb-4">
                  <span className={`font-outfit ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                    {t('total')}
                  </span>
                  <div className="text-right">
                    <span className="font-cormorant text-2xl font-bold text-[#C8102E]">
                      {formatDualPrice(totalPrice).eur} €
                    </span>
                    <span className={`block text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                      {formatDualPrice(totalPrice).bgn} {t('currency')}
                    </span>
                  </div>
                </div>
                <p className={`text-center text-sm font-outfit ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                  {t('language') === 'bg' 
                    ? 'Покажете тази поръчка на сервитьора' 
                    : 'Show this order to your waiter'}
                </p>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;


